import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { configureAuthHeader } from "../../../../api/axiosConfig";
import * as api from "../../../../api/generated-api";
import {
  getDealSignaturesForUser,
  updateDealDocumentSignatures,
} from "../../../../api/deals/signatures";
import { DealsPageLayout } from "../../dealsPageLayout/DealsPageLayout";
import { DealPageActionButtonInfo } from "../../dealsPageLayout/DealsPageHeader/DealsPageHeader";
import { Box, Typography, useTheme } from "@mui/material";
import Confetti from "react-confetti";
import { useHistory, useLocation } from "react-router-dom";
import YourSignatures from "./YourSignatures";

export const showSignAllSuccessQueryStringParam = "showSignAllSuccess";

type YourSignaturesPageProps = {
  token: string;
  user: any;
  orgId: number;
  dealId: number;
};

const YourSignaturesPage = ({
  token,
  user,
  orgId,
  dealId,
}: YourSignaturesPageProps) => {
  configureAuthHeader(token);

  const [dealDocumentSignatures, setDealDocumentSignatures] =
    useState<api.DealDocumentSignature[]>(null);
  const [showSignAllDialog, setShowSignAllDialog] = useState<boolean>(false);
  const [actionButtons, setActionButtons] = useState<
    DealPageActionButtonInfo[]
  >([]);
  const [showAllDocumentsSignedMessage, setShowAllDocumentsSignedMessage] =
    useState<boolean>(false);

  const theme = useTheme();

  const history = useHistory();

  //get deal document signatures for user
  useEffect(() => {
    getDealSignaturesForUser(orgId, dealId, user.id, (dealSignatures) => {
      setDealDocumentSignatures(dealSignatures);
    });
  }, [orgId, dealId]);

  //trigger signed all documents success via query string param
  const routeParams = new URLSearchParams(useLocation().search);
  const showSignAllSuccess = routeParams.get(
    showSignAllSuccessQueryStringParam
  );

  useEffect(() => {
    if (showSignAllSuccess) {
      setShowAllDocumentsSignedMessage(true);
    }
  }, [showSignAllSuccess]);

  //determine if sign all button should be displayed
  useEffect(() => {
    if (dealDocumentSignatures?.length) {
      const dealDocumentSignaturesUnsigned = dealDocumentSignatures.filter(
        (dealSigner) => dealSigner.status === api.SigningStatus.requested
      );

      if (dealDocumentSignaturesUnsigned.length > 0) {
        handleShowSignAllButton(true);
      }
    } else {
      handleShowSignAllButton(false);
    }
  }, [dealDocumentSignatures]);

  const handleShowSignAllButton = (isDisplayed: boolean) => {
    if (isDisplayed) {
      setActionButtons([
        {
          label: "Sign All",
          primary: true,
          dataTestId: "sign-all-documents-button",
          onClick: () => {
            setShowSignAllDialog(true);
          },
        },
      ]);
    } else {
      setActionButtons([]);
    }
  };

  const handleSignAllDealDocuments = () => {
    const dealDocumentSignaturesUnsigned = dealDocumentSignatures.filter(
      (dealSigner) => dealSigner.status === api.SigningStatus.requested
    );

    //update state optimistically
    const updatedDealDocumentSignatures = dealDocumentSignatures.map(
      (dealDocumentSignature) => {
        if (dealDocumentSignature.status === api.SigningStatus.requested) {
          dealDocumentSignature.status = api.SigningStatus.signed;
        }
        return dealDocumentSignature;
      }
    );

    setDealDocumentSignatures(updatedDealDocumentSignatures);

    //update the API
    const updateValues = dealDocumentSignaturesUnsigned.map(
      (dealDocumentSignature) => {
        return {
          id: dealDocumentSignature.id,
          status: api.SigningStatus.signed,
        };
      }
    );

    updateDealDocumentSignatures(
      orgId,
      dealId,
      dealDocumentSignaturesUnsigned[0]?.dealDocumentId,
      updateValues
    );

    //close the sign all dialog
    setShowSignAllDialog(false);

    //hide the sign all button
    handleShowSignAllButton(false);

    //show the success message and confetti
    setShowAllDocumentsSignedMessage(true);
  };

  const AllDocumentsSignedMessage = () => {
    return (
      <Box
        data-testid="all-documents-signed-message"
        sx={{
          backgroundColor: theme.palette.primary.light,
          p: "25px",
          mb: "25px",
        }}
      >
        <Typography variant="h2">{`Thank you for signing!`}</Typography>
        <Typography variant="body1">
          You can review{" "}
          <Typography
            variant="link"
            onClick={() => {
              history.push(`/${orgId}/deals/${dealId}/edit/signatures`);
            }}
          >
            Signature and Wire Tracking
          </Typography>{" "}
          to see an overview of deal status including other signers, investors,
          and wires.
        </Typography>
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={500}
          gravity={0.3}
          recycle={false}
          colors={[
            theme.palette.primary.darker,
            theme.palette.primary.dark,
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.secondary.dark,
          ]}
        />
      </Box>
    );
  };

  return (
    <DealsPageLayout title="Your Signatures" actionButtons={actionButtons}>
      {showAllDocumentsSignedMessage && <AllDocumentsSignedMessage />}
      <YourSignatures
        orgId={orgId}
        dealId={dealId}
        dealDocumentSignatures={dealDocumentSignatures}
        isSignButtonVisible={true}
        showSignAllDialog={showSignAllDialog}
        onSignAllDealDocuments={handleSignAllDealDocuments}
        onCancelSignAllDealDocuments={() => {
          setShowSignAllDialog(false);
        }}
      />
    </DealsPageLayout>
  );
};

const mapStateToProps = (state, props) => {
  const {
    match: {
      params: { dealId, orgId },
    },
  } = props;
  const { token, user } = state.auth;

  return {
    token,
    user,
    dealId,
    orgId,
  };
};

export default connect(mapStateToProps)(YourSignaturesPage);
